import { useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';

import {
  HeartbeatPayload,
  heartbeatService,
} from '@/services/heartbeat.service';

const HEARTBEAT_INTERVAL = 60000;
const TAB_ID_STORAGE_KEY = 'heartbeat_tab_id';
const ACTIVITY_EVENTS: Array<keyof WindowEventMap> = [
  'pointerdown',
  'keydown',
  'scroll',
  'wheel',
  'touchstart',
];

type UseActivityHeartbeatOptions = {
  enabled?: boolean;
};

export const useActivityHeartbeat = ({
  enabled = false,
}: UseActivityHeartbeatOptions = {}) => {
  const isActiveRef = useRef(false);
  const tabIdRef = useRef('');
  const closingSentRef = useRef(false);

  const { mutate } = useMutation({
    mutationFn: (payload: HeartbeatPayload) =>
      heartbeatService.sendHeartbeat(payload),
    onError: (error) => {
      console.error('Heartbeat failed', error);
    },
  });

  useEffect(() => {
    if (!enabled) {
      isActiveRef.current = false;
      closingSentRef.current = false;
      return;
    }

    tabIdRef.current = sessionStorage.getItem(TAB_ID_STORAGE_KEY) ?? uuidv4();
    sessionStorage.setItem(TAB_ID_STORAGE_KEY, tabIdRef.current);
    closingSentRef.current = false;

    const buildPayload = (isClosing = false): HeartbeatPayload => ({
      tab_id: tabIdRef.current,
      client_timestamp: new Date().toISOString(),
      page_url: `${window.location.pathname}${window.location.search}${window.location.hash}`,
      is_visible: document.visibilityState === 'visible',
      ...(isClosing ? { is_closing: true } : {}),
    });

    const sendHeartbeat = (payload: HeartbeatPayload) => {
      mutate(payload);
    };

    const flushHeartbeat = () => {
      sendHeartbeat(buildPayload());
      isActiveRef.current = false;
    };

    const sendClosingHeartbeat = () => {
      if (closingSentRef.current) {
        return;
      }

      closingSentRef.current = true;
      heartbeatService.sendClosingHeartbeat(buildPayload(true));
    };

    const markActive = () => {
      isActiveRef.current = true;
    };

    const interval = window.setInterval(() => {
      if (!isActiveRef.current) {
        return;
      }

      flushHeartbeat();
    }, HEARTBEAT_INTERVAL);

    const handlePageClose = () => {
      sendClosingHeartbeat();
    };

    const handleVisibilityChange = () => {
      flushHeartbeat();
    };

    const activityListenerOptions: AddEventListenerOptions = {
      passive: true,
    };

    for (const eventName of ACTIVITY_EVENTS) {
      window.addEventListener(eventName, markActive, activityListenerOptions);
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handlePageClose);
    window.addEventListener('pagehide', handlePageClose);

    return () => {
      window.clearInterval(interval);

      for (const eventName of ACTIVITY_EVENTS) {
        window.removeEventListener(
          eventName,
          markActive,
          activityListenerOptions,
        );
      }

      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handlePageClose);
      window.removeEventListener('pagehide', handlePageClose);

      if (enabled && isActiveRef.current) {
        flushHeartbeat();
      }
    };
  }, [enabled, mutate]);
};
